// Shipped-without-stock-deduction report (#437).
// Spec: docs/specs/2026-10-09-wms-shipped-without-stock-deduct-report-design.md
// (mdf-project) §4.1 response shape, §5 UI plan, ruling R4: ONE GET returns
// every order row; the page filters (shop), aggregates the summary
// and exports xlsx client-side, so the summary always matches the filter on
// screen. Every decision lives here, vue-free, so jest can pin it without
// mounting a .vue (this repo cannot mount .vue in jest).
//
// House rules: nothing here throws; junk input yields empty output.

/** Statuses the report can show (R1). The default is all four. */
export const SND_STATUSES = [115, 121, 122, 130];

/** Platform ids the API accepts (§4.1), labels drawn with platformLabel(). */
export const SND_PLATFORM_IDS = [1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11];

// Brand names are not translated; 1 (keyed / Modern Trade BO orders) is,
// through shipped_no_deduct.platform_self.
const PLATFORM_NAMES = {
	2: "TikTok",
	3: "Shopee",
	4: "Lazada",
	5: "LINE",
	6: "Facebook",
	7: "Instagram",
	8: "Pancake",
	9: "MDFStore",
	10: "Makro",
	11: "Amaze"
};

/** Order-status text keys — the same top-level keys OrderTables.vue draws. */
export const SND_STATUS_LABEL_KEYS = {
	115: "order_status_awaiting_transit",
	121: "order_status_intransit",
	122: "order_status_delivered",
	130: "order_status_completed"
};

/** Max inclusive span of the date filter (§4.1: date_to − date_from ≤ 30). */
export const SND_MAX_DAYS = 31;

/** Page size of the order tab (§5). */
export const SND_PAGE_SIZE = 50;

const VIEWS = ["pending", "acked", "all"];

function toId(v) {
	const n = Number(v);
	return Number.isFinite(n) ? n : NaN;
}

function asArray(v) {
	return Array.isArray(v) ? v : [];
}

function isOrder(o) {
	return o !== null && typeof o === "object";
}

/**
 * filterOrders(orders, { shopIds = [], view = 'pending' })
 * view: 'pending' (ack == null) | 'acked' (ack != null) | 'all'.
 * shopIds empty = every shop; otherwise match on shop_id.
 * An unknown view is treated as 'pending'. Since the §8 owner revision (#439,
 * ack UI removed) the page always passes view 'all'; the view option stays
 * because the contract pins it.
 */
export function filterOrders(orders, opts) {
	const o = opts && typeof opts === "object" ? opts : {};
	const view = VIEWS.indexOf(o.view) >= 0 ? o.view : "pending";
	const shopIds = asArray(o.shopIds).map(toId).filter(n => !Number.isNaN(n));
	return asArray(orders).filter(row => {
		if (!isOrder(row)) return false;
		if (shopIds.length && shopIds.indexOf(toId(row.shop_id)) < 0) return false;
		const acked = row.ack !== null && row.ack !== undefined;
		if (view === "pending") return !acked;
		if (view === "acked") return acked;
		return true;
	});
}

/**
 * summarizeByProduct(orders) -> [{ wms_product_id, code, name, barcode,
 *   quantity, order_count, product_deleted }]
 * Skips mapping_missing orders (E1). Grouped by wms_product_id (0 / missing ids
 * are skipped); order_count = distinct orders containing the product. Sorted by
 * quantity desc, then code asc.
 */
export function summarizeByProduct(orders) {
	const byId = new Map();
	asArray(orders).forEach(order => {
		if (!isOrder(order) || order.mapping_missing) return;
		asArray(order.items).forEach(it => {
			if (!it || typeof it !== "object") return;
			const pid = toId(it.wms_product_id);
			if (!pid || Number.isNaN(pid)) return;
			const qty = Number(it.quantity);
			let g = byId.get(pid);
			if (!g) {
				g = {
					wms_product_id: pid,
					code: it.code || "",
					name: it.name || "",
					barcode: it.barcode || "",
					quantity: 0,
					order_count: 0,
					product_deleted: !!it.product_deleted,
					_orders: new Set()
				};
				byId.set(pid, g);
			}
			g.quantity += Number.isFinite(qty) ? qty : 0;
			g._orders.add(order.id);
		});
	});
	const out = [];
	byId.forEach(g => {
		g.order_count = g._orders.size;
		delete g._orders;
		out.push(g);
	});
	out.sort((a, b) => {
		if (b.quantity !== a.quantity) return b.quantity - a.quantity;
		const ac = String(a.code);
		const bc = String(b.code);
		return ac < bc ? -1 : ac > bc ? 1 : 0;
	});
	return out;
}

/** Footer of the summary tab: { quantity, order_count (distinct orders) }. */
export function summaryTotals(orders) {
	const ids = new Set();
	let quantity = 0;
	asArray(orders).forEach(order => {
		if (!isOrder(order) || order.mapping_missing) return;
		let counted = false;
		asArray(order.items).forEach(it => {
			if (!it || !toId(it.wms_product_id)) return;
			const q = Number(it.quantity);
			quantity += Number.isFinite(q) ? q : 0;
			counted = true;
		});
		if (counted) ids.add(order.id);
	});
	return { quantity, order_count: ids.size };
}

/** Total pieces across orders; mapping-missing = 0. */
export function totalQuantity(orders) {
	return summaryTotals(orders).quantity;
}

/**
 * Fixed column order of the product × channel pivot (§8): marketplaces first,
 * then the storefront, then the rest. Ids not listed sort after, ascending.
 */
export const SND_PLATFORM_ORDER = [2, 3, 4, 9, 11, 10, 1, 5, 6, 7, 8];

function platformRank(id) {
	const i = SND_PLATFORM_ORDER.indexOf(id);
	return i >= 0 ? i : SND_PLATFORM_ORDER.length + id;
}

/**
 * summarizeByProductAndPlatform(orders) — §8 product × channel pivot.
 * -> { platforms: [platform_id… fixed order, only those present],
 *      rows: [{ wms_product_id, code, name, barcode, product_deleted,
 *               by_platform: { [platform_id]: qty }, quantity, order_count }],
 *      totals: { by_platform, quantity, order_count } }
 * mapping_missing orders are skipped, as in summarizeByProduct. "Present" =
 * the platform has at least one counted item. Every row's by_platform carries
 * EVERY listed platform; a product not sold on a platform has 0 there (the
 * page draws 0 as "–"). order_count = distinct orders containing the product;
 * totals.order_count = distinct orders counted at all. Sort: quantity desc,
 * then code asc (same as summarizeByProduct).
 */
export function summarizeByProductAndPlatform(orders) {
	const byId = new Map();
	const present = new Set();
	const totalByPlatform = {};
	const allOrders = new Set();
	let totalQty = 0;
	asArray(orders).forEach(order => {
		if (!isOrder(order) || order.mapping_missing) return;
		const pf = toId(order.platform_id);
		if (Number.isNaN(pf)) return;
		asArray(order.items).forEach(it => {
			if (!it || typeof it !== "object") return;
			const pid = toId(it.wms_product_id);
			if (!pid || Number.isNaN(pid)) return;
			const q = Number(it.quantity);
			const qty = Number.isFinite(q) ? q : 0;
			let g = byId.get(pid);
			if (!g) {
				g = {
					wms_product_id: pid,
					code: it.code || "",
					name: it.name || "",
					barcode: it.barcode || "",
					product_deleted: !!it.product_deleted,
					by_platform: {},
					quantity: 0,
					order_count: 0,
					_orders: new Set()
				};
				byId.set(pid, g);
			}
			g.by_platform[pf] = (g.by_platform[pf] || 0) + qty;
			g.quantity += qty;
			g._orders.add(order.id);
			present.add(pf);
			totalByPlatform[pf] = (totalByPlatform[pf] || 0) + qty;
			totalQty += qty;
			allOrders.add(order.id);
		});
	});
	const platforms = Array.from(present).sort((a, b) => platformRank(a) - platformRank(b));
	const rows = [];
	byId.forEach(g => {
		const bp = {};
		platforms.forEach(p => { bp[p] = g.by_platform[p] || 0; });
		g.by_platform = bp;
		g.order_count = g._orders.size;
		delete g._orders;
		rows.push(g);
	});
	rows.sort((a, b) => {
		if (b.quantity !== a.quantity) return b.quantity - a.quantity;
		const ac = String(a.code);
		const bc = String(b.code);
		return ac < bc ? -1 : ac > bc ? 1 : 0;
	});
	const tbp = {};
	platforms.forEach(p => { tbp[p] = totalByPlatform[p] || 0; });
	return { platforms, rows, totals: { by_platform: tbp, quantity: totalQty, order_count: allOrders.size } };
}

/** reason -> i18n key; unknown reasons fall back to no_packing_close. */
export function reasonLabelKey(reason) {
	if (reason === "closed_without_items") return "shipped_no_deduct.reason.closed_without_items";
	return "shipped_no_deduct.reason.no_packing_close";
}

/** Platform label: brand name, or i18n for 1, or "#id" for an unknown id. */
export function platformLabel(id, t) {
	const n = toId(id);
	if (n === 1) {
		const key = "shipped_no_deduct.platform_self";
		const tr = typeof t === "function" ? t(key) : "";
		return tr && tr !== key ? tr : "BO";
	}
	return PLATFORM_NAMES[n] || `#${id}`;
}

/** Distinct shops of the loaded rows, sorted by name: [{ id, name }]. */
export function shopOptions(orders) {
	const seen = new Map();
	asArray(orders).forEach(o => {
		if (!isOrder(o)) return;
		const id = toId(o.shop_id);
		if (Number.isNaN(id) || seen.has(id)) return;
		seen.set(id, { id, name: o.shop_name || `#${o.shop_id}` });
	});
	return Array.from(seen.values()).sort((a, b) => (a.name < b.name ? -1 : a.name > b.name ? 1 : a.id - b.id));
}

/** Count of mapping-missing orders (yellow banner, E1). */
export function mappingMissingCount(orders) {
	return asArray(orders).filter(o => isOrder(o) && o.mapping_missing).length;
}

function parseYMD(s) {
	if (typeof s !== "string" || !/^\d{4}-\d{2}-\d{2}$/.test(s)) return null;
	const [y, m, d] = s.split("-").map(Number);
	const t = Date.UTC(y, m - 1, d);
	const back = new Date(t);
	if (back.getUTCFullYear() !== y || back.getUTCMonth() !== m - 1 || back.getUTCDate() !== d) return null;
	return t;
}

/** Inclusive day count between two YYYY-MM-DD strings; -1 if invalid/reversed. */
export function rangeDays(from, to) {
	const a = parseYMD(from);
	const b = parseYMD(to);
	if (a === null || b === null || b < a) return -1;
	return Math.round((b - a) / 86400000) + 1;
}

/** True when [from, to] is a valid range of 1..SND_MAX_DAYS days. */
export function isRangeAllowed(from, to) {
	const d = rangeDays(from, to);
	return d >= 1 && d <= SND_MAX_DAYS;
}

function ymd(date) {
	const y = date.getFullYear();
	const m = String(date.getMonth() + 1).padStart(2, "0");
	const d = String(date.getDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

/** Default window: last 7 days ending today (local time). [from, to]. */
export function defaultRange(now) {
	const today = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
	const from = new Date(today.getFullYear(), today.getMonth(), today.getDate() - 6);
	return [ymd(from), ymd(today)];
}

const BANGKOK_OFFSET_MS = 7 * 60 * 60 * 1000; // Asia/Bangkok = UTC+7, no DST
const DAY_MS_UTC = 24 * 60 * 60 * 1000;

function ymdUTC(date) {
	const y = date.getUTCFullYear();
	const m = String(date.getUTCMonth() + 1).padStart(2, "0");
	const d = String(date.getUTCDate()).padStart(2, "0");
	return `${y}-${m}-${d}`;
}

/**
 * #441 default window: yesterday -> today, as Asia/Bangkok calendar days,
 * whatever the browser's time zone. [from, to] as 'YYYY-MM-DD'.
 * An invalid `now` falls back to the current time.
 */
export function defaultShippedNoDeductRange(now = new Date()) {
	const at = now instanceof Date && !Number.isNaN(now.getTime()) ? now : new Date();
	const bkk = at.getTime() + BANGKOK_OFFSET_MS;
	return [ymdUTC(new Date(bkk - DAY_MS_UTC)), ymdUTC(new Date(bkk))];
}

/**
 * Query params for GET /api/v2/wms/report/shipped-no-deduct (§4.1).
 * Empty platform list = all platforms (param omitted); empty status list =
 * the default four.
 */
export function buildQuery({ dateFrom, dateTo, platformIds, statuses } = {}) {
	const q = { date_from: dateFrom, date_to: dateTo };
	const p = asArray(platformIds).map(toId).filter(n => SND_PLATFORM_IDS.indexOf(n) >= 0);
	if (p.length && p.length < SND_PLATFORM_IDS.length) q.platform_ids = p.join(",");
	const s = asArray(statuses).map(toId).filter(n => SND_STATUSES.indexOf(n) >= 0);
	q.statuses = (s.length ? s : SND_STATUSES).join(",");
	return q;
}

/** `data` of an axios response (envelope Datum, as InventoryReturnBalance). */
export function responseData(resp) {
	const body = resp && resp.data;
	if (!body || typeof body !== "object") return { generated_at: "", orders: [] };
	const d = body.Datum && typeof body.Datum === "object" ? body.Datum
		: body.data && typeof body.data === "object" ? body.data : body;
	return {
		generated_at: typeof d.generated_at === "string" ? d.generated_at : "",
		orders: asArray(d.orders).filter(isOrder)
	};
}

// Report-specific error codes -> shipped_no_deduct.errors.*. The BE may send
// `errors.range_too_wide` or the bare/uppercase code (R6 names
// REPORT_INDEX_MISSING). apiErrorMessage would hide a 503 behind the generic
// server message, so these are checked first.
const ERROR_CODES = ["range_too_wide", "too_many_rows", "report_index_missing", "invalid_status", "invalid_request"];

/** i18n key for a report error, or "" when the generic handler should run. */
export function errorKey(err) {
	const data = err && err.response && err.response.data;
	if (!data || typeof data !== "object") return "";
	const raw = String(data.Msg || data.message || data.Cause || "").trim().toLowerCase().replace(/^errors\./, "");
	return ERROR_CODES.indexOf(raw) >= 0 ? `shipped_no_deduct.errors.${raw}` : "";
}

/**
 * Sheet 1 (§8): the product × channel pivot, array-of-arrays incl. header and
 * a totals row. Same numbers as the summary tab (0 where not sold).
 */
export function summarySheetRows(orders, t) {
	const tr = typeof t === "function" ? t : k => k;
	const pv = summarizeByProductAndPlatform(orders);
	const header = [
		tr("shipped_no_deduct.col_code"),
		tr("shipped_no_deduct.col_name"),
		...pv.platforms.map(p => platformLabel(p, tr)),
		tr("shipped_no_deduct.total"),
		tr("shipped_no_deduct.col_order_count")
	];
	const rows = pv.rows.map(g => [
		g.code,
		g.product_deleted ? `${g.name} ${tr("shipped_no_deduct.product_deleted")}` : g.name,
		...pv.platforms.map(p => g.by_platform[p]),
		g.quantity,
		g.order_count
	]);
	const foot = [
		tr("shipped_no_deduct.total"),
		"",
		...pv.platforms.map(p => pv.totals.by_platform[p]),
		pv.totals.quantity,
		pv.totals.order_count
	];
	return [header, ...rows, foot];
}

/** Sheet 2 rows: one per order × item; mapping-missing = one row (§5 Excel). */
export function orderSheetRows(orders, t) {
	const tr = typeof t === "function" ? t : k => k;
	const header = [
		tr("shipped_no_deduct.col_order_id"),
		tr("shipped_no_deduct.col_platform_order_id"),
		tr("shipped_no_deduct.col_platform"),
		tr("shipped_no_deduct.col_shop"),
		tr("shipped_no_deduct.col_status"),
		tr("shipped_no_deduct.col_created_at"),
		tr("shipped_no_deduct.col_reason"),
		tr("shipped_no_deduct.col_code"),
		tr("shipped_no_deduct.col_name"),
		tr("shipped_no_deduct.col_qty_short")
	];
	const out = [header];
	asArray(orders).forEach(o => {
		if (!isOrder(o)) return;
		const statusKey = SND_STATUS_LABEL_KEYS[toId(o.order_status)];
		const base = [
			o.id,
			o.platform_order_id || "",
			platformLabel(o.platform_id, tr),
			o.shop_name || "",
			statusKey ? tr(statusKey) : String(o.order_status),
			o.created_at || "",
			tr(reasonLabelKey(o.reason))
		];
		const items = asArray(o.items);
		if (o.mapping_missing || !items.length) {
			out.push([...base, "", tr("shipped_no_deduct.mapping_missing"), ""]);
			return;
		}
		items.forEach(it => {
			const name = it.product_deleted ? `${it.name || ""} ${tr("shipped_no_deduct.product_deleted")}` : it.name || "";
			out.push([...base, it.code || "", name, Number(it.quantity) || 0]);
		});
	});
	return out;
}

/** Excel file name: shipped-no-deduct_<from>_<to>.xlsx */
export function exportFileName(from, to) {
	return `shipped-no-deduct_${from}_${to}.xlsx`;
}
